<template>
  <div class="space-y-6">
    <!-- Selector Rápido de Depósitos / Almacenes -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
        <div class="flex items-center gap-2">
          <i class="fa fa-warehouse text-orange-500 text-sm" />
          <span class="text-xs font-bold uppercase tracking-wider text-gray-500">
            Filtrar por Depósito / Almacén:
          </span>
        </div>
        <span class="text-xs text-gray-400">
          Mostrando existencias físicas en tiempo real
        </span>
      </div>

      <div class="flex flex-wrap gap-2">
        <!-- Botón: Todos los depósitos -->
        <button
          type="button"
          class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer"
          :class="[
            currentDepositId === 'all'
              ? 'bg-orange-500 text-white shadow-sm ring-2 ring-orange-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
          @click="selectDeposit('all')"
        >
          <i class="fa fa-city text-xs" />
          <span>Todos los depósitos</span>
        </button>

        <!-- Botones por cada depósito específico -->
        <button
          v-for="deposit in deposits"
          :key="deposit.id"
          type="button"
          class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer"
          :class="[
            String(currentDepositId) === String(deposit.id)
              ? 'bg-orange-500 text-white shadow-sm ring-2 ring-orange-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
          @click="selectDeposit(deposit.id)"
        >
          <i class="fa fa-warehouse text-xs" />
          <span>{{ deposit.name }}</span>
          <span 
            v-if="deposit.alias" 
            class="text-[10px] opacity-75 font-normal"
          >
            ({{ deposit.alias }})
          </span>
        </button>
      </div>
    </div>

    <!-- KPIs del Depósito Seleccionado -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- KPI 1: Total Productos -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
        <div>
          <p class="text-xs font-bold uppercase tracking-wider text-gray-400">Total Productos</p>
          <p class="text-2xl font-black text-gray-900 mt-1">
            {{ formatNumber(stats.total_products) }}
          </p>
          <span class="text-[11px] text-gray-500">Registrados en inventario</span>
        </div>
        <div class="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl shrink-0">
          <i class="fa fa-boxes-stacked" />
        </div>
      </div>

      <!-- KPI 2: Unidades Físicas -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
        <div>
          <p class="text-xs font-bold uppercase tracking-wider text-gray-400">Unidades en Stock</p>
          <p class="text-2xl font-black text-orange-600 mt-1">
            {{ formatNumber(stats.total_units) }}
          </p>
          <span class="text-[11px] text-gray-500">Unidades físicas disponibles</span>
        </div>
        <div class="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center text-xl shrink-0">
          <i class="fa fa-cubes" />
        </div>
      </div>

      <!-- KPI 3: Valor Costo Total -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
        <div>
          <p class="text-xs font-bold uppercase tracking-wider text-gray-400">Valorización Costo</p>
          <p class="text-2xl font-black text-slate-800 mt-1">
            {{ formatCurrency(stats.total_cost_value) }}
          </p>
          <span class="text-[11px] text-gray-500">Inversión en mercancía</span>
        </div>
        <div class="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center text-xl shrink-0">
          <i class="fa fa-dollar-sign" />
        </div>
      </div>

      <!-- KPI 4: Valor Venta Total -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
        <div>
          <p class="text-xs font-bold uppercase tracking-wider text-gray-400">Valorización Venta</p>
          <p class="text-2xl font-black text-emerald-600 mt-1">
            {{ formatCurrency(stats.total_sale_value) }}
          </p>
          <div class="flex items-center gap-1.5 mt-0.5">
            <span class="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold">
              Margen ~{{ stats.potential_margin || 0 }}%
            </span>
          </div>
        </div>
        <div class="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl shrink-0">
          <i class="fa fa-chart-line" />
        </div>
      </div>
    </div>

    <!-- Barra de Filtros y Acciones de Reporte -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div class="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
        <!-- Buscador y Selectores -->
        <div class="flex flex-col sm:flex-row flex-1 gap-3 items-stretch sm:items-center">
          <!-- Búsqueda por texto -->
          <div class="relative flex-1">
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar producto por nombre o código..."
              class="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              @input="onSearchInput"
            />
          </div>

          <!-- Filtro Categoría -->
          <div class="w-full sm:w-48">
            <select
              v-model="selectedCategory"
              class="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              @change="applyFilters"
            >
              <option value="all">Todas las categorías</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>

          <!-- Filtro Estado de Stock -->
          <div class="w-full sm:w-44">
            <select
              v-model="selectedStatus"
              class="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              @change="applyFilters"
            >
              <option value="all">Todos los stocks</option>
              <option value="in_stock">Con existencias (>0)</option>
              <option value="low_stock">Bajo stock (≤5)</option>
              <option value="out_of_stock">Sin existencias (0)</option>
            </select>
          </div>

          <!-- Botón Limpiar Filtros -->
          <button
            v-if="hasActiveFilters"
            type="button"
            class="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-orange-600 bg-gray-100 hover:bg-orange-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer shrink-0"
            title="Restablecer filtros"
            @click="clearFilters"
          >
            <i class="fa fa-times text-xs" />
            <span>Limpiar</span>
          </button>
        </div>

        <!-- Botones de Exportación / Reportes del Depósito -->
        <div class="flex items-center gap-2 shrink-0">
          <!-- Exportar Excel -->
          <a
            :href="excelExportUrl"
            class="px-3 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-xs font-bold transition-all flex items-center gap-2 shadow-sm"
            title="Exportar reporte de existencias a Excel"
          >
            <i class="fa fa-file-excel text-emerald-600" />
            <span>Excel</span>
          </a>

          <!-- Exportar PDF -->
          <a
            :href="pdfExportUrl"
            target="_blank"
            class="px-3 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-bold transition-all flex items-center gap-2 shadow-sm"
            title="Generar reporte PDF imprimible con membrete"
          >
            <i class="fa fa-file-pdf text-rose-600" />
            <span>PDF</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Tabla de Existencias por Depósito -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm text-gray-600">
          <thead class="bg-gray-50/75 border-b border-gray-100 text-xs font-bold uppercase tracking-wider text-gray-500">
            <tr>
              <th scope="col" class="px-5 py-3.5">Producto</th>
              <th scope="col" class="px-4 py-3.5">Categoría</th>
              <th scope="col" class="px-4 py-3.5">Depósito / Almacén</th>
              <th scope="col" class="px-4 py-3.5 text-center">Stock Actual</th>
              <th scope="col" class="px-4 py-3.5 text-right">Costo Unit.</th>
              <th scope="col" class="px-4 py-3.5 text-right">Precio Venta</th>
              <th scope="col" class="px-4 py-3.5 text-right">Total Costo</th>
              <th scope="col" class="px-4 py-3.5 text-right">Total Venta</th>
              <th scope="col" class="px-4 py-3.5 text-center">Margen</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="item in products.data"
              :key="item.id"
              class="hover:bg-orange-50/30 transition-colors"
            >
              <!-- Producto -->
              <td class="px-5 py-3.5">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden shrink-0 flex items-center justify-center">
                    <img
                      v-if="item.photo_url"
                      :src="item.photo_url"
                      :alt="item.name"
                      class="w-full h-full object-cover"
                    />
                    <i v-else class="fa fa-cube text-gray-400 text-base" />
                  </div>
                  <div>
                    <div class="font-bold text-gray-900 hover:text-orange-600 transition-colors">
                      {{ item.name }}
                    </div>
                    <div class="font-mono text-xs text-gray-400 mt-0.5">
                      {{ item.code }}
                    </div>
                  </div>
                </div>
              </td>

              <!-- Categoría -->
              <td class="px-4 py-3.5">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                  {{ item.category }}
                </span>
              </td>

              <!-- Depósito -->
              <td class="px-4 py-3.5">
                <div class="flex items-center gap-1.5">
                  <i class="fa fa-warehouse text-gray-400 text-xs" />
                  <span class="font-medium text-gray-800">{{ item.deposit_name }}</span>
                </div>
              </td>

              <!-- Stock Actual -->
              <td class="px-4 py-3.5 text-center">
                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold"
                  :class="[
                    item.stock > 5
                      ? 'bg-emerald-100 text-emerald-800'
                      : item.stock > 0
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-rose-100 text-rose-800'
                  ]"
                >
                  <span 
                    class="w-1.5 h-1.5 rounded-full mr-1.5"
                    :class="[
                      item.stock > 5
                        ? 'bg-emerald-500'
                        : item.stock > 0
                          ? 'bg-amber-500'
                          : 'bg-rose-500'
                    ]"
                  />
                  {{ formatNumber(item.stock) }} und.
                </span>
              </td>

              <!-- Costo Unitario -->
              <td class="px-4 py-3.5 text-right font-medium text-gray-600">
                {{ formatCurrency(item.unit_cost) }}
              </td>

              <!-- Precio Venta -->
              <td class="px-4 py-3.5 text-right font-semibold text-gray-900">
                {{ formatCurrency(item.unit_price) }}
              </td>

              <!-- Total Costo -->
              <td class="px-4 py-3.5 text-right font-bold text-slate-700">
                {{ formatCurrency(item.total_cost) }}
              </td>

              <!-- Total Venta -->
              <td class="px-4 py-3.5 text-right font-bold text-emerald-600">
                {{ formatCurrency(item.total_sale) }}
              </td>

              <!-- Margen -->
              <td class="px-4 py-3.5 text-center">
                <span 
                  class="text-xs font-bold"
                  :class="[
                    item.margin_percent > 30 
                      ? 'text-emerald-600' 
                      : item.margin_percent > 15 
                        ? 'text-blue-600' 
                        : 'text-amber-600'
                  ]"
                >
                  {{ item.margin_percent }}%
                </span>
              </td>
            </tr>

            <!-- Estado Vacío -->
            <tr v-if="!products.data || products.data.length === 0">
              <td colspan="9" class="px-6 py-12 text-center text-gray-400">
                <div class="flex flex-col items-center justify-center">
                  <div class="w-16 h-16 rounded-full bg-orange-50 text-orange-400 flex items-center justify-center text-2xl mb-3">
                    <i class="fa fa-boxes-stacked" />
                  </div>
                  <h3 class="text-base font-bold text-gray-700 mb-1">
                    No hay productos en este depósito
                  </h3>
                  <p class="text-xs text-gray-400 max-w-sm">
                    No se encontraron productos registrados con existencias según los filtros seleccionados.
                  </p>
                  <button
                    v-if="hasActiveFilters"
                    type="button"
                    class="mt-4 px-3.5 py-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                    @click="clearFilters"
                  >
                    Restablecer Filtros
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div 
        v-if="products.links && products.links.length > 3" 
        class="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50/50"
      >
        <div class="text-xs text-gray-500">
          Mostrando 
          <span class="font-bold text-gray-700">{{ products.from || 0 }}</span> 
          a 
          <span class="font-bold text-gray-700">{{ products.to || 0 }}</span> 
          de 
          <span class="font-bold text-gray-700">{{ products.total || 0 }}</span> 
          registros
        </div>

        <div class="flex flex-wrap items-center gap-1">
          <Link
            v-for="(link, key) in products.links"
            :key="key"
            :href="link.url || '#'"
            :class="[
              'px-3 py-1.5 text-xs rounded-lg transition-colors',
              link.active
                ? 'bg-orange-500 text-white font-bold shadow-sm'
                : link.url
                  ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-transparent'
            ]"
            preserve-scroll
            preserve-state
            v-html="link.label"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import debounce from 'lodash/debounce'

export default {
  name: 'InventoryStockTab',

  components: {
    Link,
  },

  props: {
    deposits: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    products: {
      type: Object,
      required: true,
    },
    stats: {
      type: Object,
      default: () => ({
        total_products: 0,
        total_units: 0,
        total_cost_value: 0,
        total_sale_value: 0,
        potential_margin: 0,
      }),
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      currentDepositId: this.filters.deposit_id || 'all',
      searchQuery: this.filters.search || '',
      selectedCategory: this.filters.category_id || 'all',
      selectedStatus: this.filters.stock_status || 'all',
    }
  },

  computed: {
    hasActiveFilters() {
      return (
        this.currentDepositId !== 'all' ||
        (this.searchQuery && this.searchQuery.trim() !== '') ||
        this.selectedCategory !== 'all' ||
        this.selectedStatus !== 'all'
      )
    },

    excelExportUrl() {
      const params = new URLSearchParams()
      params.append('deposit_id', this.currentDepositId)
      if (this.selectedCategory !== 'all') params.append('category_id', this.selectedCategory)
      if (this.selectedStatus !== 'all') params.append('stock_status', this.selectedStatus)
      if (this.searchQuery) params.append('search', this.searchQuery)
      return `/inventories/deposit-stock/export?${params.toString()}`
    },

    pdfExportUrl() {
      const params = new URLSearchParams()
      params.append('deposit_id', this.currentDepositId)
      if (this.selectedCategory !== 'all') params.append('category_id', this.selectedCategory)
      if (this.selectedStatus !== 'all') params.append('stock_status', this.selectedStatus)
      if (this.searchQuery) params.append('search', this.searchQuery)
      return `/inventories/deposit-stock/pdf?${params.toString()}`
    },
  },

  methods: {
    selectDeposit(depositId) {
      this.currentDepositId = depositId
      this.applyFilters()
    },

    onSearchInput: debounce(function () {
      this.applyFilters()
    }, 400),

    applyFilters() {
      const query = {
        tab: 'stock',
        deposit_id: this.currentDepositId,
      }

      if (this.searchQuery && this.searchQuery.trim() !== '') {
        query.search = this.searchQuery.trim()
      }
      if (this.selectedCategory && this.selectedCategory !== 'all') {
        query.category_id = this.selectedCategory
      }
      if (this.selectedStatus && this.selectedStatus !== 'all') {
        query.stock_status = this.selectedStatus
      }

      this.$inertia.get('/inventories', query, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    },

    clearFilters() {
      this.currentDepositId = 'all'
      this.searchQuery = ''
      this.selectedCategory = 'all'
      this.selectedStatus = 'all'
      this.applyFilters()
    },

    formatNumber(val) {
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(val || 0)
    },

    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(val || 0)
    },
  },
}
</script>
